import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

export interface CustomerDebtSummaryItem {
  customerId: string;
  ma_kh: string;
  ten_kh: string;
  shortName?: string;
  phone?: string;
  no_dau_ky: number;
  phat_sinh_ky: number;
  da_thanh_toan_ky: number;
  no_cuoi_ky: number;
  details?: Array<{
    id: string;
    ngay: Date | string;
    dien_giai: string;
    so_luong?: number | null;
    don_gia?: number | null;
    tang: number;
    giam: number;
    so_ct: string;
  }>;
}

export interface CustomerDebtSummaryPrintContentProps {
  reportData: {
    summary: {
      totalOpeningBalance: number;
      totalIncurred: number;
      totalPaid: number;
      totalClosingBalance: number;
      totalCustomers: number;
    };
    results: CustomerDebtSummaryItem[];
  };
  dateRange?: { from?: string; to?: string };
  config?: any;
  showDetails?: boolean;
}

export default function CustomerDebtSummaryPrintContent({
  reportData,
  dateRange,
  config,
  showDetails = false,
}: CustomerDebtSummaryPrintContentProps) {
  const { summary, results = [] } = reportData || { summary: {}, results: [] };
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (customerId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatNumber = (val?: number | string) => {
    if (val === null || val === undefined || val === '') return '0';
    if (isNaN(Number(val))) return String(val);
    return Number(val).toLocaleString('vi-VN');
  };

  return (
    <div
      id="print-area"
      className="kh-print-doc w-full max-w-[280mm] bg-white text-black p-[8mm] font-serif mx-auto text-[9.5pt] leading-tight print:p-0 print:m-0"
      style={{ fontFamily: '"Times New Roman", Times, serif' }}
    >
      {/* ========== HEADER COMPANY & ISO BADGE ========== */}
      <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-3">
        <div className="flex-shrink-0 flex items-center gap-2 w-[110px]">
          {config?.logoUrl ? (
            <img
              src={config.logoUrl}
              alt="Logo"
              className="h-[50px] max-w-[110px] object-contain"
            />
          ) : (
            <div className="h-[50px] w-[95px] flex items-center justify-center border border-slate-400 bg-slate-50 text-slate-700 font-bold text-[11px]">
              APV PACK
            </div>
          )}
        </div>

        <div className="text-center flex-1 px-3">
          <div className="text-[19px] font-bold uppercase tracking-wide text-slate-900">
            BÁO CÁO TỔNG HỢP CÔNG NỢ KHÁCH HÀNG
          </div>
          <div className="text-[10pt] text-slate-700 mt-1 font-semibold">
            <span>Kỳ đối soát: </span>
            <strong>{dateRange?.from ? formatDate(dateRange.from) : 'Đầu kỳ'}</strong>
            <span> — </span>
            <strong>{dateRange?.to ? formatDate(dateRange.to) : 'Hiện tại'}</strong>
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-col items-end justify-center w-[110px] text-[8pt] text-slate-600 text-right">
          <div className="border border-slate-300 rounded px-1.5 py-1 bg-slate-50">
            <div className="font-semibold text-slate-800">BM-TC-02</div>
            <div>Ban hành: 01/2025</div>
          </div>
        </div>
      </div>

      {/* ========== SUMMARY CARDS (Screen overview) ========== */}
      <div className="grid grid-cols-4 gap-2 mb-3 print:hidden text-white text-xs">
        <div className="bg-gradient-to-br from-slate-600 to-slate-800 rounded p-2.5 shadow-xs">
          <div className="text-[10.5px] opacity-90 font-medium">Nợ Đầu Kỳ</div>
          <div className="text-base font-bold mt-0.5">
            {formatNumber(summary?.totalOpeningBalance)} ₫
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded p-2.5 shadow-xs">
          <div className="text-[10.5px] opacity-90 font-medium">Phát Sinh Trong Kỳ (+)</div>
          <div className="text-base font-bold mt-0.5">
            {formatNumber(summary?.totalIncurred)} ₫
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded p-2.5 shadow-xs">
          <div className="text-[10.5px] opacity-90 font-medium">Đã Thanh Toán (-)</div>
          <div className="text-base font-bold mt-0.5">
            {formatNumber(summary?.totalPaid)} ₫
          </div>
        </div>
        <div className="bg-gradient-to-br from-rose-600 to-red-700 rounded p-2.5 shadow-xs">
          <div className="text-[10.5px] opacity-90 font-medium">Nợ Cuối Kỳ</div>
          <div className="text-base font-bold mt-0.5">
            {formatNumber(summary?.totalClosingBalance)} ₫
          </div>
        </div>
      </div>

      {/* ========== MAIN REPORT TABLE ========== */}
      <table className="w-full border-collapse border border-black text-[9pt]">
        <thead>
          <tr className="bg-slate-800 text-white font-bold text-center text-[9pt] print:bg-slate-200 print:text-black">
            <th className="border border-black py-1.5 px-1 w-[10mm]">STT</th>
            <th className="border border-black py-1.5 px-2 text-left">Tên Khách Hàng / Mã KH</th>
            <th className="border border-black py-1.5 px-2 text-right w-[40mm]">Nợ Đầu Kỳ (₫)</th>
            <th className="border border-black py-1.5 px-2 text-right w-[42mm]">Phát Sinh Trong Kỳ (₫)</th>
            <th className="border border-black py-1.5 px-2 text-right w-[42mm]">Thanh Toán Trong Kỳ (₫)</th>
            <th className="border border-black py-1.5 px-2 text-right w-[42mm]">Nợ Cuối Kỳ (₫)</th>
          </tr>
        </thead>
        <tbody>
          {results.length === 0 ? (
            <tr>
              <td colSpan={6} className="border border-black py-8 text-center text-slate-500 italic">
                Không có dữ liệu công nợ khách hàng trong khoảng thời gian này
              </td>
            </tr>
          ) : (
            results.map((item, idx) => {
              const isExpanded = showDetails || expandedRows[item.customerId];
              const hasDebt = item.no_cuoi_ky > 0;

              return (
                <React.Fragment key={item.customerId || idx}>
                  {/* Master Customer Row */}
                  <tr
                    onClick={() => toggleRow(item.customerId)}
                    className="hover:bg-slate-100 transition-colors cursor-pointer break-inside-avoid"
                  >
                    <td className="border border-black py-1 px-1 text-center font-mono text-[8.5pt]">
                      {idx + 1}
                    </td>
                    <td className="border border-black py-1 px-2 text-left font-semibold text-slate-900">
                      <div className="flex items-center gap-1">
                        <span className="print:hidden text-slate-400">
                          {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        </span>
                        <span>{item.ten_kh}</span>
                        <span className="text-[8pt] text-slate-500 font-mono font-normal">
                          ({item.ma_kh})
                        </span>
                      </div>
                    </td>
                    <td className="border border-black py-1 px-2 text-right font-mono">
                      {formatNumber(item.no_dau_ky)}
                    </td>
                    <td className="border border-black py-1 px-2 text-right font-mono font-medium text-amber-700">
                      {formatNumber(item.phat_sinh_ky)}
                    </td>
                    <td className="border border-black py-1 px-2 text-right font-mono font-medium text-emerald-700">
                      {formatNumber(item.da_thanh_toan_ky)}
                    </td>
                    <td className={`border border-black py-1 px-2 text-right font-mono font-bold ${
                      hasDebt ? 'text-rose-700' : 'text-slate-700'
                    }`}>
                      {formatNumber(item.no_cuoi_ky)}
                    </td>
                  </tr>

                  {/* Drill-down Detail Subtable (if expanded or showDetails is on) */}
                  {isExpanded && item.details && item.details.length > 0 && (
                    <tr className="bg-slate-50/70 break-inside-avoid">
                      <td colSpan={6} className="border border-black p-2 bg-slate-50">
                        <div className="border border-slate-300 rounded bg-white overflow-hidden">
                          <table className="w-full border-collapse text-[8pt]">
                            <thead>
                              <tr className="bg-slate-100 font-semibold text-slate-700 text-center border-b border-slate-300">
                                <th className="p-1 w-8">#</th>
                                <th className="p-1 w-20 text-center">Ngày</th>
                                <th className="p-1 text-left">Diễn giải</th>
                                <th className="p-1 w-16 text-right">SL</th>
                                <th className="p-1 w-20 text-right">Đơn giá</th>
                                <th className="p-1 w-24 text-right text-amber-800">Phát sinh (₫)</th>
                                <th className="p-1 w-24 text-right text-emerald-800">Thanh toán (₫)</th>
                                <th className="p-1 w-24 text-center">Số CT</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.details.map((d, dIdx) => (
                                <tr key={d.id || dIdx} className="border-b border-slate-200 hover:bg-slate-50">
                                  <td className="p-1 text-center font-mono">{dIdx + 1}</td>
                                  <td className="p-1 text-center">{formatDate(d.ngay)}</td>
                                  <td className="p-1 text-left font-medium">{d.dien_giai}</td>
                                  <td className="p-1 text-right font-mono">{d.so_luong ? formatNumber(d.so_luong) : '—'}</td>
                                  <td className="p-1 text-right font-mono">{d.don_gia ? formatNumber(d.don_gia) : '—'}</td>
                                  <td className="p-1 text-right font-mono text-amber-700 font-semibold">
                                    {d.tang > 0 ? formatNumber(d.tang) : '—'}
                                  </td>
                                  <td className="p-1 text-right font-mono text-emerald-700 font-semibold">
                                    {d.giam > 0 ? formatNumber(d.giam) : '—'}
                                  </td>
                                  <td className="p-1 text-center font-mono text-[7.5pt]">{d.so_ct || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}

          {/* Grand Total Row */}
          {results.length > 0 && (
            <tr className="bg-slate-200 font-bold text-slate-900 border-t-2 border-black break-inside-avoid">
              <td colSpan={2} className="border border-black py-2 px-2 text-right uppercase">
                TỔNG CỘNG ({results.length} khách hàng):
              </td>
              <td className="border border-black py-2 px-2 text-right font-mono text-[9.5pt]">
                {formatNumber(summary?.totalOpeningBalance)}
              </td>
              <td className="border border-black py-2 px-2 text-right font-mono text-[9.5pt] text-amber-800">
                {formatNumber(summary?.totalIncurred)}
              </td>
              <td className="border border-black py-2 px-2 text-right font-mono text-[9.5pt] text-emerald-800">
                {formatNumber(summary?.totalPaid)}
              </td>
              <td className="border border-black py-2 px-2 text-right font-mono text-[10pt] text-rose-800 font-extrabold">
                {formatNumber(summary?.totalClosingBalance)}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ========== SIGNATURE SECTION ========== */}
      <div className="grid grid-cols-4 gap-4 mt-6 text-center text-[9pt] break-inside-avoid">
        <div>
          <div className="font-bold uppercase text-slate-900">NGƯỜI LẬP BIỂU</div>
          <div className="text-[8pt] text-slate-500 italic mb-14">(Ký, ghi rõ họ tên)</div>
          <div className="font-semibold text-slate-800">Nhân Viên Kế Toán</div>
        </div>
        <div>
          <div className="font-bold uppercase text-slate-900">KẾ TOÁN CÔNG NỢ</div>
          <div className="text-[8pt] text-slate-500 italic mb-14">(Ký, ghi rõ họ tên)</div>
          <div className="font-semibold text-slate-800">Phụ Trách AR</div>
        </div>
        <div>
          <div className="font-bold uppercase text-slate-900">KẾ TOÁN TRƯỞNG</div>
          <div className="text-[8pt] text-slate-500 italic mb-14">(Ký, ghi rõ họ tên)</div>
          <div className="font-semibold text-slate-800">Trưởng Phòng Kế Toán</div>
        </div>
        <div>
          <div className="font-bold uppercase text-slate-900">BAN GIÁM ĐỐC</div>
          <div className="text-[8pt] text-slate-500 italic mb-14">(Ký, duyệt)</div>
          <div className="font-semibold text-slate-800">Giám Đốc Tài Chính / CEO</div>
        </div>
      </div>
    </div>
  );
}
